import React from "react";
import { RealizedProductCardProps } from "./RealizedProductCard.types";
import { useFormatAmount } from "../../../hooks/useFormatAmount";
import styled from "@emotion/styled";
import FlexRow from "../../../components/Layout/FlexRow/FlexRow";
import FlexColumn from "../../../components/Layout/FlexColumn/FlexColumn";
import { StyledImage } from "../../../components/Image/Image";
import { Body5RegularTypography } from "../../../primitives/Typography/typographyVariants";
import { Body6RegularTypography } from "../../../primitives/Typography/typographyVariants";
import { Heading4SemiboldTypography } from "../../../primitives/Typography/typographyVariants";

const StyledCard = styled(FlexRow)`
  align-self: stretch;
`;
const StyledColumn = styled(FlexColumn)`
  align-self: stretch;
`;

const getReadableValue = (val: number | string | null | undefined): string => {
  if (val == null) return "0";
  const floatVal = parseFloat(String(val));
  if (!floatVal || isNaN(floatVal)) return "0";
  const absVal = Math.abs(floatVal);
  if (absVal >= 1000) {
    return `${(floatVal / 1000).toFixed(2)}K`;
  }
  return String(val);
};

export const RealizedProductCard: React.FC<RealizedProductCardProps> = ({
  growth,
  index = 0,
  onCardClick,
  className,
  dataCy,
  earningColor = "#28AD2D",
  showClicks = true,
}) => {
  const formattedEarning = useFormatAmount(growth?.earning, true, false);

  const handleClick = () => {
    if (onCardClick) {
      onCardClick(growth, index);
    }
  };

  const isHighlighted =
    growth?.total_earnings && growth?.potential_earning
      ? growth.total_earnings > growth.potential_earning
      : false;

  return (
    <FlexRow
      width="100%"
      p={6}
      gap={8}
      borderRadius={4}
      border="1px solid #D7D7D7"
      backgroundColor={
        isHighlighted
          ? "background.positive.tertiary"
          : "background.default.default"
      }
      onClick={handleClick}
      key={growth.id}
      data-idx={index}
      className={className}
      id={dataCy || `growth-earnings-product-card-${index}`}
      alignItems="flex-start"
    >
      <StyledImage
        src={growth?.product?.thumbnail_url || ""}
        alt={growth?.product?.name || "product"}
        width="40px"
        height="54px"
        borderRadius="2px"
        objectFit="cover"
        border="0.8px solid #D7D7D7"
      />
      <StyledCard
        alignItems="flex-start"
        width="100%"
        justifyContent="space-between"
        flex={1}
      >
        <StyledColumn width="unset" flex={1}>
          <Body5RegularTypography
            fontFamily="Lexend"
            color="text.default.default"
            textTransform="uppercase"
            lineClamp={1}
            textOverflow="ellipsis"
            maxWidth="100%"
          >
            {growth?.product?.brand || ""}
          </Body5RegularTypography>
          <Body5RegularTypography
            fontFamily="Lexend"
            color="text.default.default"
            lineClamp={2}
            textOverflow="ellipsis"
            maxWidth="100%"
          >
            {growth?.product?.name || ""}
          </Body5RegularTypography>
        </StyledColumn>
        <StyledColumn
          alignItems="flex-end"
          justifyContent="center"
          width="unset"
          flex={1}
        >
          <Heading4SemiboldTypography
            fontFamily="Lexend"
            color={earningColor}
            textAlign="right"
          >
            ₹ {formattedEarning.fullFormattedAmount}
          </Heading4SemiboldTypography>
          {showClicks && (
            <Body6RegularTypography
              fontFamily="Lexend"
              color="text.default.default"
              textAlign="right"
            >
              click: {getReadableValue(growth?.total_clicks)}
            </Body6RegularTypography>
          )}
        </StyledColumn>
      </StyledCard>
    </FlexRow>
  );
};
